
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Review } from "@/types/review";
import type { AirportStats } from "@/types/airport";

interface Airport {
  id: number;
  name: string;
  iata_code: string | null;
  location: string;
}

export function useAirportDetails(code: string | undefined) {
  const [airport, setAirport] = useState<Airport | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [airportStats, setAirportStats] = useState<AirportStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFavorited, setIsFavorited] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const navigate = useNavigate();

  // First, check if user is logged in
  useEffect(() => {
    const checkUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data.user) {
        setUserId(data.user.id);
      }
    };
    
    checkUser();
  }, []);

  // Fetch airport data
  useEffect(() => {
    const fetchAirport = async () => {
      if (!code) return;
      
      try {
        // Decode the URL parameter first
        const decodedCode = decodeURIComponent(code);
        console.log("Fetching airport with decoded code:", decodedCode);
        
        // If code appears to be numeric (legacy ID), try to fetch by ID
        if (!isNaN(Number(decodedCode))) {
          const numericId = parseInt(decodedCode, 10);
          const { data: airportData, error: airportError } = await supabase
            .from('airports')
            .select('*')
            .eq('id', numericId)
            .single();

          if (!airportError && airportData) {
            setAirport(airportData);
            
            // If we found an airport by ID, redirect to the code-based URL
            if (airportData.iata_code) {
              navigate(`/airport/${airportData.iata_code}`, { replace: true });
              return;
            }
          }
        } else {
          // First try to fetch by IATA code
          const { data: airportData, error: airportError } = await supabase
            .from('airports')
            .select('*')
            .eq('iata_code', decodedCode)
            .single();

          if (!airportError && airportData) {
            setAirport(airportData);
          } else {
            // If IATA code fails, try to fetch by exact name
            const { data: airportByNameData, error: airportByNameError } = await supabase
              .from('airports')
              .select('*')
              .eq('name', decodedCode)
              .maybeSingle();

            if (!airportByNameError && airportByNameData) {
              setAirport(airportByNameData);
              
              // If we found an airport by name and it has an IATA code, redirect to the code-based URL
              if (airportByNameData.iata_code) {
                navigate(`/airport/${airportByNameData.iata_code}`, { replace: true });
                return;
              }
            } else {
              console.log("Trying partial name matching for:", decodedCode);
              
              // Last attempt - try matching on name using ilike for partial/case-insensitive matching
              const { data: airportByPartialNameData, error: airportByPartialNameError } = await supabase
                .from('airports')
                .select('*')
                .ilike('name', `%${decodedCode}%`)
                .order('name', { ascending: true })
                .limit(1);

              if (!airportByPartialNameError && airportByPartialNameData && airportByPartialNameData.length > 0) {
                console.log("Found airport by partial name:", airportByPartialNameData[0]);
                setAirport(airportByPartialNameData[0]);
                
                // If we found an airport by partial name and it has an IATA code, redirect to the code-based URL
                if (airportByPartialNameData[0].iata_code) {
                  navigate(`/airport/${airportByPartialNameData[0].iata_code}`, { replace: true });
                  return;
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching airport:', error);
      }
    };
    
    fetchAirport();
  }, [code, navigate]);

  // Fetch favorites status separately if user is logged in
  useEffect(() => {
    const fetchFavoriteStatus = async () => {
      if (!airport || !userId) return;
      
      try {
        const { data: favoriteData, error: favoriteError } = await supabase
          .from('airport_favorites')
          .select('id')
          .eq('airport_id', airport.id)
          .eq('user_id', userId)
          .maybeSingle();

        if (favoriteError) throw favoriteError;
        setIsFavorited(!!favoriteData);
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    };
    
    fetchFavoriteStatus();
  }, [airport, userId]);

  // Fetch reviews and calculate stats after airport data is loaded
  useEffect(() => {
    const fetchReviewsAndStats = async () => {
      if (!airport) return;
      
      try {
        // Fetch all reviews to calculate accurate rankings
        const { data: allReviews, error: allReviewsError } = await supabase
          .from('reviews')
          .select('*');

        if (allReviewsError) throw allReviewsError;

        // Group reviews by airport name and calculate statistics
        const airportStatsMap = new Map();
        
        allReviews.forEach((review: Review) => {
          if (!airportStatsMap.has(review.airport_name)) {
            airportStatsMap.set(review.airport_name, {
              name: review.airport_name,
              scores: [],
              handChecks: [],
              reviews: []
            });
          }
          
          const airportStat = airportStatsMap.get(review.airport_name);
          airportStat.scores.push(review.film_friendly_score);
          airportStat.handChecks.push(review.hand_check_success);
          airportStat.reviews.push(review);
        });

        // Calculate averages and format for ranking
        const rankedAirports = Array.from(airportStatsMap.entries())
          .map(([name, stats]) => {
            const totalReviews = stats.reviews.length;
            const totalScores = stats.scores.reduce((a: number, b: number) => a + b, 0);
            const totalHandChecks = stats.handChecks.filter(Boolean).length;
            
            return {
              name,
              averageScore: totalReviews ? parseFloat((totalScores / totalReviews).toFixed(2)) : 0,
              handCheckRate: totalReviews ? parseFloat(((totalHandChecks / totalReviews) * 100).toFixed(1)) : 0,
              totalReviews: totalReviews
            };
          })
          .filter(airport => airport.totalReviews >= 1) // At least 1 review to be ranked
          .sort((a, b) => {
            // First sort by hand check success rate
            const rateDiff = b.handCheckRate - a.handCheckRate;
            if (Math.abs(rateDiff) > 0.01) return rateDiff;
            
            // Then by average score
            const scoreDiff = b.averageScore - a.averageScore;
            if (Math.abs(scoreDiff) > 0.01) return scoreDiff;
            
            // Finally by number of reviews as a tiebreaker
            return b.totalReviews - a.totalReviews;
          });

        // Find current airport's rank
        const currentAirportIndex = rankedAirports.findIndex(a => a.name === airport.name);
        
        // Extract stats for the current airport
        if (currentAirportIndex >= 0) {
          const currentAirportStats = rankedAirports[currentAirportIndex];
          setAirportStats({
            // Add 1 to make rank 1-based
            rank: currentAirportIndex + 1, 
            handCheckRate: currentAirportStats.handCheckRate || 0,
            totalReviews: currentAirportStats.totalReviews || 0,
            averageScore: currentAirportStats.averageScore || 0
          });
        } else {
          // If airport not found in rankings, set default stats
          setAirportStats({
            rank: 0,
            handCheckRate: 0,
            totalReviews: 0,
            averageScore: 0
          });
        }

        // Fetch reviews for the specific airport with user profiles
        const { data: reviewsData, error: reviewsError } = await supabase
          .from('reviews')
          .select(`
            *,
            profiles(username, verification_status, is_creative),
            likes_count:review_likes(count)
          `)
          .eq('airport_name', airport.name)
          .order('created_at', { ascending: false });

        if (reviewsError) throw reviewsError;

        const reviewsWithLikes = reviewsData?.map(review => ({
          ...review,
          likes_count: review.likes_count?.[0]?.count || 0
        })) || [];

        setReviews(reviewsWithLikes);
      } catch (error) {
        console.error('Error fetching reviews and stats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (airport) {
      fetchReviewsAndStats();
    }
  }, [airport]);

  return {
    airport,
    reviews,
    airportStats,
    isLoading,
    isFavorited,
    setIsFavorited
  };
}
